// Admin platform-fee rules — the take-rate, scoped by category and/or city.
// The most specific effective rule wins at quote time; with no rule, the
// default applies. Used by quotes + the merchant quote-builder preview.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, catalog, type FeeRule } from '../../lib/api';

const usd = (minor: number) => `$${(minor / 100).toFixed(2)}`;
const scopeLabel = (r: FeeRule) =>
  r.category?.label && r.city?.name ? `${r.category.label} · ${r.city.name}`
  : r.category?.label ? r.category.label
  : r.city?.name ? `${r.city.name} (all categories)`
  : 'All orders (global)';

export function AdminFeesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'fees'], queryFn: admin.feeRules });
  const catsQ = useQuery({ queryKey: ['catalog', 'categories'], queryFn: catalog.categories });
  const citiesQ = useQuery({ queryKey: ['catalog', 'cities'], queryFn: catalog.cities });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'fees'] });

  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [cityId, setCityId] = useState<number | ''>('');
  const [pct, setPct] = useState('');
  const [flat, setFlat] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');

  const create = useMutation({
    mutationFn: () => admin.createFee({
      category_id: categoryId === '' ? null : Number(categoryId),
      city_id: cityId === '' ? null : Number(cityId),
      fee_pct_bps: Math.round(Number(pct) * 100),
      fee_flat_usd_minor: Math.round(Number(flat || 0) * 100),
      min_fee_usd_minor: Math.round(Number(min || 0) * 100),
      max_fee_usd_minor: max ? Math.round(Number(max) * 100) : null,
    }),
    onSuccess: () => { setPct(''); setFlat(''); setMin(''); setMax(''); setCategoryId(''); setCityId(''); invalidate(); toast.success('Fee rule added'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });
  const remove = useMutation({
    mutationFn: (id: number) => admin.deleteFee(id),
    onSuccess: () => { invalidate(); toast.success('Rule deleted'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  const def = q.data?.default;

  return (
    <PortalShell nav={ADMIN_NAV} activeId="fees" title="Platform fees" subtitle="The take-rate, scoped by category and/or city. Most specific rule wins.">
      <div className="space-y-5 max-w-4xl">
        {/* Default */}
        <Card className="bg-slate-50 border-slate-200">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Default (when no rule matches)</p>
          <p className="mt-1 text-[15px] text-slate-900">
            {def ? <><span className="font-semibold">{(def.fee_pct_bps / 100).toFixed(1)}%</span> of goods+delivery{def.fee_flat_usd_minor ? ` + ${usd(def.fee_flat_usd_minor)}` : ''}, min {usd(def.min_fee_usd_minor)}</> : '—'}
          </p>
        </Card>

        {/* Add */}
        <Card padding="p-0">
          <div className="p-5 border-b border-slate-100"><SectionHeader title="Add a fee rule" subtitle="Leave category/city blank for a wider scope. Percent is of goods + delivery; flat & min/max are USD." /></div>
          <div className="px-5 py-3 flex flex-wrap items-end gap-2 bg-slate-50/60">
            <Sel label="Category" value={categoryId} onChange={setCategoryId} options={(catsQ.data ?? []).map((c) => ({ v: c.id, l: c.label }))} anyLabel="All categories" />
            <Sel label="City" value={cityId} onChange={setCityId} options={(citiesQ.data ?? []).map((c) => ({ v: c.id, l: c.name }))} anyLabel="All cities" />
            <Num label="Fee %" value={pct} onChange={setPct} placeholder="12" w="w-20" />
            <Num label="+ Flat $" value={flat} onChange={setFlat} placeholder="0" w="w-20" />
            <Num label="Min $" value={min} onChange={setMin} placeholder="1.50" w="w-20" />
            <Num label="Max $" value={max} onChange={setMax} placeholder="—" w="w-20" />
            <Button size="sm" onClick={() => create.mutate()} loading={create.isPending} disabled={!pct} leadingIcon={<Plus className="w-3.5 h-3.5" />}>Add</Button>
          </div>

          {q.isLoading ? (
            <div className="text-center py-10 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : (q.data?.rules ?? []).length === 0 ? (
            <EmptyState icon={Percent} title="No fee rules" body="The default applies until you add one." />
          ) : (
            <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr><th className="text-left py-2.5 px-5 font-medium">Scope</th><th className="text-right py-2.5 px-3 font-medium">Fee %</th><th className="text-right py-2.5 px-3 font-medium">Flat</th><th className="text-right py-2.5 px-3 font-medium">Min</th><th className="text-right py-2.5 px-3 font-medium">Max</th><th className="text-right py-2.5 px-5 font-medium"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(q.data?.rules ?? []).map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-5 font-semibold text-slate-900">{scopeLabel(r)}</td>
                    <td className="py-2.5 px-3 text-right font-mono">{(r.fee_pct_bps / 100).toFixed(1)}%</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">{r.fee_flat_usd_minor ? usd(r.fee_flat_usd_minor) : '—'}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">{usd(r.min_fee_usd_minor)}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">{r.max_fee_usd_minor != null ? usd(r.max_fee_usd_minor) : '—'}</td>
                    <td className="py-2.5 px-5 text-right"><button onClick={() => { if (window.confirm('Delete this fee rule?')) remove.mutate(r.id); }} className="p-1 rounded text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function Sel({ label, value, onChange, options, anyLabel }: { label: string; value: number | ''; onChange: (v: number | '') => void; options: Array<{ v: number; l: string }>; anyLabel: string }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <select value={value === '' ? '' : String(value)} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1 block px-2 py-1.5 text-[13px] border border-slate-200 rounded-md bg-white max-w-[160px]">
        <option value="">{anyLabel}</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function Num({ label, value, onChange, placeholder, w }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; w: string }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`mt-1 ${w} px-2 py-1.5 text-[13px] font-mono text-right border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30`} />
    </label>
  );
}
